import type { PhoneAuthStep } from '@/types/phone-auth-flow';
import { useMemo, useState } from 'react';

interface PhoneAuthFlowState {
  authStep: PhoneAuthStep;
  phoneNumber: string;
  userName: string;
  verificationCode: string;
}

interface UsePhoneAuthFlowParams {
  initialPhoneNumber?: string;
  initialStep?: PhoneAuthStep;
  storageKey?: string;
  initialUserName?: string;
  initialVerificationCode?: string;
}

const PHONE_AUTH_STEPS = new Set<PhoneAuthStep>(['phone', 'code', 'name']);

function canUseSessionStorage() {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function isPhoneAuthStep(value: unknown): value is PhoneAuthStep {
  return typeof value === 'string' && PHONE_AUTH_STEPS.has(value as PhoneAuthStep);
}

function buildPhoneAuthFlowState(params: Required<Omit<UsePhoneAuthFlowParams, 'storageKey'>>): PhoneAuthFlowState {
  return {
    authStep: params.initialStep,
    phoneNumber: params.initialPhoneNumber,
    userName: params.initialUserName,
    verificationCode: params.initialVerificationCode,
  };
}

function readPersistedPhoneAuthFlow(storageKey?: string): Partial<PhoneAuthFlowState> {
  if (!storageKey || !canUseSessionStorage()) {
    return {};
  }

  try {
    const rawValue = window.sessionStorage.getItem(storageKey);

    if (!rawValue) {
      return {};
    }

    const parsedValue = JSON.parse(rawValue);

    if (typeof parsedValue !== 'object' || parsedValue === null) {
      return {};
    }

    const flowState = parsedValue as Partial<Record<keyof PhoneAuthFlowState, unknown>>;

    return {
      authStep: isPhoneAuthStep(flowState.authStep) ? flowState.authStep : undefined,
      phoneNumber: typeof flowState.phoneNumber === 'string' ? flowState.phoneNumber : undefined,
      userName: typeof flowState.userName === 'string' ? flowState.userName : undefined,
      verificationCode: typeof flowState.verificationCode === 'string' ? flowState.verificationCode : undefined,
    };
  } catch {
    return {};
  }
}

function clearPersistedPhoneAuthFlow(storageKey?: string) {
  if (!storageKey || !canUseSessionStorage()) {
    return;
  }

  window.sessionStorage.removeItem(storageKey);
}

function persistPhoneAuthFlow(storageKey: string, state: PhoneAuthFlowState) {
  if (!canUseSessionStorage()) {
    return;
  }

  window.sessionStorage.setItem(storageKey, JSON.stringify(state));
}

function isSamePhoneAuthFlowState(left: PhoneAuthFlowState, right: PhoneAuthFlowState) {
  return (
    left.authStep === right.authStep &&
    left.phoneNumber === right.phoneNumber &&
    left.userName === right.userName &&
    left.verificationCode === right.verificationCode
  );
}

export function usePhoneAuthFlow({
  initialPhoneNumber = '',
  initialStep = 'phone',
  storageKey,
  initialUserName = '',
  initialVerificationCode = '',
}: UsePhoneAuthFlowParams = {}) {
  const defaultState = useMemo(
    () =>
      buildPhoneAuthFlowState({
        initialPhoneNumber,
        initialStep,
        initialUserName,
        initialVerificationCode,
      }),
    [initialPhoneNumber, initialStep, initialUserName, initialVerificationCode],
  );
  const [state, setState] = useState<PhoneAuthFlowState>(() => {
    const persistedState = readPersistedPhoneAuthFlow(storageKey);

    return {
      authStep: persistedState.authStep ?? defaultState.authStep,
      phoneNumber: persistedState.phoneNumber ?? defaultState.phoneNumber,
      userName: persistedState.userName ?? defaultState.userName,
      verificationCode: persistedState.verificationCode ?? defaultState.verificationCode,
    };
  });

  const commitState = (getNextState: (currentState: PhoneAuthFlowState) => PhoneAuthFlowState) => {
    setState((currentState) => {
      const nextState = getNextState(currentState);

      if (!storageKey) {
        return nextState;
      }

      if (isSamePhoneAuthFlowState(nextState, defaultState)) {
        clearPersistedPhoneAuthFlow(storageKey);
        return nextState;
      }

      persistPhoneAuthFlow(storageKey, nextState);

      return nextState;
    });
  };

  const reset = () => {
    if (!storageKey) {
      setState(defaultState);
      return;
    }

    clearPersistedPhoneAuthFlow(storageKey);
    setState(defaultState);
  };

  return {
    authStep: state.authStep,
    phoneNumber: state.phoneNumber,
    reset,
    setAuthStep: (authStep: PhoneAuthStep) => {
      commitState((currentState) => ({ ...currentState, authStep }));
    },
    setPhoneNumber: (phoneNumber: string) => {
      commitState((currentState) => ({ ...currentState, phoneNumber }));
    },
    setUserName: (userName: string) => {
      commitState((currentState) => ({ ...currentState, userName }));
    },
    setVerificationCode: (verificationCode: string) => {
      commitState((currentState) => ({ ...currentState, verificationCode }));
    },
    userName: state.userName,
    verificationCode: state.verificationCode,
  };
}
